import type {
    ProfileNavigationItemProps,
    ShorthandCollection,
} from '@archipro-design/aria';
import { pxToRem, useStyles, ProfileNavigation } from '@archipro-design/aria';
import { generateURL, parseURL } from '@archipro-website/top-navigation';
import * as S from './TitleSectionFilter.mobile.style';
import type { FilterStates } from '~/modules/search/type/filter-state';
import type { ElasticSearchCategories } from '~/modules/search/type/api';
import { CATEGORY_IGNORE_FILTER_KEYS } from '../category-filter-section/CategoryFilterSection';
import { useSearchFilters } from '~/modules/search/hook/use-search-filters';
import { useToggle } from 'ahooks';
import FilterDrawer from '~/modules/search/components/filter-drawer/FilterDrawer';
import { useSearchSorts } from '~/modules/search/hook/use-search-sorts';
import { Link, useLocation } from '@remix-run/react';
import { createNavigationFilters } from '~/modules/search/hook/use-mobile-profile-navigation-items';
import { useTracker } from '@archipro-website/tracker';
interface TitleSectionFilterMobileProps {
    esCategory: ElasticSearchCategories;
    initialStates: FilterStates | null;
    quickFiltersBefore?: ShorthandCollection<ProfileNavigationItemProps>;
    hasMarketingView?: boolean;
}

const TitleSectionFilterMobile = ({
    esCategory,
    initialStates,
    hasMarketingView = false,
    quickFiltersBefore,
}: TitleSectionFilterMobileProps) => {
    const location = useLocation();
    const { basePath, params } = parseURL(location.pathname + location.search);

    const [
        filterDrawerOpen,
        { toggle: toggleFilterDrawer, set: setFilterDrawerState },
    ] = useToggle(false);

    const { css } = useStyles();

    const quickFilters: ShorthandCollection<ProfileNavigationItemProps> = [];

    const searchFilters = useSearchFilters({
        category: esCategory,
        initialStates,
        isCategoryPage: true,
        ignoreFilterKeys: CATEGORY_IGNORE_FILTER_KEYS,
    });
    const { filters, filterStates, appliedFilters, resetFilters } =
        searchFilters;

    const appliedFilterCount = appliedFilters?.length ?? 0;
    createNavigationFilters(searchFilters, quickFilters);

    if (quickFiltersBefore) {
        quickFilters.unshift(...quickFiltersBefore);
    }

    const { sortOptions } = useSearchSorts({ category: esCategory });
    sortOptions.forEach((sortOption, index) => {
        delete params['page'];
        const newURL = generateURL(basePath, {
            ...params,
            sort: sortOption.value ? [sortOption.value] : [],
        });
        quickFilters.push({
            children: sortOption.name,
            selected: sortOption.selected,
            as: Link,
            to: newURL,
            preventScrollReset: true,
            styles: {
                marginRight:
                    index + 1 === sortOptions.length ? pxToRem(18) : 'unset',
            },
        });
    });

    const tracker = useTracker();
    const logSortEvent = (event: string) => {
        tracker.log('SortClick', {
            url: new URL(window.location.href),
            data: {
                ExtraData: { sortBy: event },
            },
        });
    };
    return (
        <>
            <ProfileNavigation
                className={css(S.MobileProfileNavigation)}
                items={quickFilters}
                logSortEvent={logSortEvent}
                filterButton={{
                    children: `Filters${
                        appliedFilterCount ? `(${appliedFilterCount})` : ''
                    }`,
                    onClick: toggleFilterDrawer,
                }}
            />
            {filters && initialStates && (
                <FilterDrawer
                    open={filterDrawerOpen}
                    onOpenChange={(_, data) => setFilterDrawerState(!!data)}
                    filterSections={filters}
                    filterStates={filterStates}
                    category={esCategory}
                    onApplyFilters={(_, data) => {
                        setFilterDrawerState(false);
                        resetFilters(data ?? {});
                    }}
                    ignoreFilterKeys={CATEGORY_IGNORE_FILTER_KEYS}
                    hasMarketingView={hasMarketingView}
                />
            )}
        </>
    );
};

export default TitleSectionFilterMobile;
